import {
  closestCorners,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { restrictToParentElement } from '@dnd-kit/modifiers'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  ActionIcon,
  Box,
  Card,
  Group,
  Popover,
  Switch,
  ThemeIcon,
  Tooltip,
} from '@mantine/core'
import { useLocalStorage } from '@mantine/hooks'
import { PropsWithChildren, ReactNode, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { RxDragHandleDots2 } from 'react-icons/rx'
import { TbList } from 'react-icons/tb'
import { z } from 'zod'
import { TDatatable } from '.'
import classes from './DatatableFilter.module.css'

export type DatatableFilterProps<TItem> = {
  datatableId: string
  headers: TDatatable<TItem>[]
  children: (headers: TDatatable<TItem>[]) => ReactNode
  initialPreset?: string[]
  leftSection?: ReactNode
  rightSection?: ReactNode
}

/***
 Component used to filter out visible columns in datatable.
 pass headers object you would normally pass to <Datatable /> component,
 then render <Datatable /> as children replacing headers object with filteredHeaders provided by this component.

 datatableId: localStorage key for persisting presets.
 initialPreset?: array of header items' ids, use if you don't want to show all headers by default.

 Example of usage:
 <DatatableFilter
    datatableId={`unique-datatable-identifier`}
    headers={headers}
>
  {filteredHeaders => (
    <Datatable
      headers={filteredHeaders}
      {...yourOtherDatatableProps}
    />
  )}
</DatatableFilter>
***/
export const DatatableFilter = <TItem,>({
  headers,
  children,
  datatableId,
  initialPreset,
  leftSection,
  rightSection,
}: DatatableFilterProps<TItem>) => {
  const { t } = useTranslation()
  const [headerIds, setHeaderIds] = useLocalStorage({
    key: `${datatableId}-order`,
    deserialize: value => z.array(z.string()).parse(JSON.parse(value ?? '[]')),
    defaultValue: headers.map(h => h.id),
  })
  const [datatablePreset, setDatatablePreset] = useLocalStorage({
    key: `${datatableId}-columns`,
    deserialize: value => z.array(z.string()).parse(JSON.parse(value ?? '[]')),
    defaultValue: initialPreset ?? headerIds,
  })

  const sortedHeaders = useMemo(
    () =>
      headerIds.reduce<TDatatable<TItem>[]>(
        (acc, item) => [...acc, headers.find(h => h.id === item)!],
        [],
      ),
    [headerIds, headers],
  )

  const filteredHeaders = useMemo(
    () => sortedHeaders.filter(h => datatablePreset.includes(h.id)),
    [datatablePreset, sortedHeaders],
  )

  return (
    <Card className={classes.shell}>
      <Group justify="right" className={classes.toolbar}>
        <Box mr="auto">{leftSection}</Box>
        {rightSection}
        <Popover width={250} withArrow shadow="md" position="left-start">
          <Popover.Target>
            <Tooltip label={t('generic.filterColumns')}>
              <ActionIcon variant={'filled'} color="brand">
                <TbList size="1.5rem" />
              </ActionIcon>
            </Tooltip>
          </Popover.Target>
          <Popover.Dropdown>
            <ColumnsDnD headerIds={headerIds} setHeaderIds={setHeaderIds}>
              <Switch.Group
                value={datatablePreset}
                onChange={setDatatablePreset}
              >
                <Group className={classes.columns}>
                  {sortedHeaders.map(header => (
                    <ColumnSwitch
                      key={header.id}
                      label={header.label}
                      id={header.id}
                    />
                  ))}
                </Group>
              </Switch.Group>
            </ColumnsDnD>
          </Popover.Dropdown>
        </Popover>
      </Group>
      {children(filteredHeaders)}
    </Card>
  )
}

const ColumnSwitch = ({ id, label }: { id: string; label: ReactNode }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.8 : 1,
  }

  return (
    <Group ref={setNodeRef} style={style} className={classes.column}>
      <ThemeIcon
        variant="light"
        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
        {...attributes}
        {...listeners}
      >
        <RxDragHandleDots2 />
      </ThemeIcon>
      <Switch label={label} value={id} />
    </Group>
  )
}

const ColumnsDnD = ({
  children,
  headerIds,
  setHeaderIds,
}: PropsWithChildren<{
  headerIds: string[]
  setHeaderIds: (val: string[] | ((prevState: string[]) => string[])) => void
}>) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (!over || !over.id) return
    if (active.id === over.id) return
    if (!active.data.current) return

    const index = headerIds.findIndex(h => h === active.id)
    const newIndex = headerIds.findIndex(h => h === over.id)

    const newColumns = arrayMove(headerIds, index, newIndex)

    setHeaderIds(newColumns)
  }

  return (
    <DndContext
      modifiers={[restrictToParentElement]}
      onDragEnd={handleDragEnd}
      collisionDetection={closestCorners}
      sensors={sensors}
    >
      <SortableContext strategy={verticalListSortingStrategy} items={headerIds}>
        {children}
      </SortableContext>
    </DndContext>
  )
}
